import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, theme } from '@vimily/bonjoro-ui-components';
import { useGlobalEvent } from 'beautiful-react-hooks';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { queryKeys as tagQueryKeys, useCreateTag, useDeleteTag, useTags, useUpdateTags } from '~/App/Api/Tag/hooks';
import Modal from '~/App/Components/Modal';
import { SpinnerNew as Spinner} from '~/App/Components/Shared/Spinner';
import { TAG_CREATE, TAG_DELETE, TAG_EDIT_SAVE } from '~/generated/AnalyticsTypes';
import useAnalytics from '~/react-hooks/use-analytics';
import { Tag, TaggableResourcePage } from '~/types/models/tags';
import DeleteConfirmationModal from '../../../Components/Modals/DeleteConfirmationModal';
import AddTag from './AddTag';
import TagColorPicker from './TagColorPicker';
import { defaultTagColors } from './TagColorPicker/TagColorRadioButton';
import TagList from './TagList';

const defaultColor = defaultTagColors.turquoise;

export enum EditTagsModalEvents {
  Open = 'edit-tags-modal::open',
  Close = 'edit-tags-modal::close',
}

export const openEditTagsModal = (sourcePage: TaggableResourcePage) => {
  window.dispatchEvent(new CustomEvent(EditTagsModalEvents.Open, { detail:  { page: sourcePage } }));
};

export const closeEditTagsModal = () => {
  window.dispatchEvent(new Event(EditTagsModalEvents.Close));
};

export default function EditTagsModal() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const onModalOpen = useGlobalEvent<CustomEvent<{ page: TaggableResourcePage }>>(EditTagsModalEvents.Open as string as  keyof WindowEventMap);

  const onModalClose = useGlobalEvent(EditTagsModalEvents.Close as string as keyof WindowEventMap);

  const [taggableResourcePage, setTaggableResourcePage] = useState<TaggableResourcePage>();
  const analytics = useAnalytics();

  const handleCloseModal = () => setIsOpen(false);

  onModalOpen((event) => {
    setIsOpen(true);
    setTaggableResourcePage(event.detail.page);
  });

  onModalClose(handleCloseModal);

  type ModalStyleProps = {
    base: Record<string, unknown>;
  }

  const modalProps = {
    isOpen: isOpen,
    onRequestClose: handleCloseModal,
    hideActions: true,
    styles: {
      overlay: ({ base }: ModalStyleProps) => ({
        ...base,
        width: '100%',
        display: 'flex',
        zIndex: 9980,
      }),
      content: ({ base }: ModalStyleProps) => ({
        ...base,
        maxWidth: 400,
        width: '100%',
        padding: 'unset',
        margin: 'auto',
      }),
      panel: ({ base }: ModalStyleProps) => ({
        ...base,
        width: '100%',
        height: 'auto',
        position: 'relative',
        border: 'unset',
        overflow: 'hidden',
        borderRadius: 5,
        backgroundColor: theme.colors.white,
        padding: 10,
        marginBottom: 0,
      }),
    },
  };

  const { data: tags = [] } = useTags();

  const [tagToDelete, setTagToDelete] = useState<Tag>();
  const [color, setColor] = useState<string>(defaultColor);
  const [name, setName] = useState<string>('');

  const [updatedTags, setUpdatedTags] = useState<Tag[]>([]);

  const onDeleteClose = () => setTagToDelete(undefined);

  const setTags = (tags: Tag[]) =>
    queryClient.setQueryData(tagQueryKeys.lists(), tags);

  const { mutateAsync: createTagMutation, isLoading: creating } = useCreateTag({
    onSuccess: (tag) => {
      setTags([tag, ...tags]);
      setColor(defaultColor);
      setName('');

      analytics.track(TAG_CREATE, {
        context: taggableResourcePage,
        name: tag.name
      });
    }
  });

  const { mutateAsync: updateTagsMutation, isLoading: updating } = useUpdateTags({
    onSuccess: () => {
      const newTags = [...tags];
      for (const updatedTag of updatedTags) {
        const index = tags.findIndex(
          (currentTag) => updatedTag.uuid === currentTag.uuid
        );
        newTags[index] = updatedTag;
      }
      setTags(newTags);
      setUpdatedTags([]);

      analytics.track(TAG_EDIT_SAVE, {
        context: taggableResourcePage
      });
    }
  });

  const { mutateAsync: deleteTagMutation, isLoading: deleting } = useDeleteTag({
    onSuccess: () => {
      if(tagToDelete) {
        setTags(tags.filter((currentTag) => currentTag.uuid !== tagToDelete.uuid));
        onDeleteClose();

        analytics.track(TAG_DELETE, {
          context: taggableResourcePage,
          name: tagToDelete?.name
        });
      }
    }
  });

  const createTag = () => void createTagMutation({ name, color });

  const updateTags = () => updateTagsMutation({ updatedTags });

  const onDeleteConfirm = () => {
    if(!tagToDelete) return;

    deleteTagMutation({ tag: tagToDelete });
  };

  const onTagNameChangeHandler =  useCallback((tag: Tag, name: string) => {
    const index = updatedTags.findIndex((currentTag) => tag.uuid === currentTag.uuid);
    const newUpdatedTag = {...tag, name} as Tag;

    if(index < 0) {
      setUpdatedTags([...updatedTags, newUpdatedTag]);
    } else {
      if(tag.name !== name) {
        updatedTags[index] = newUpdatedTag;
        setUpdatedTags(updatedTags);
      } else {
        setUpdatedTags(updatedTags.filter((currentTag) => currentTag.uuid !== tag.uuid));
      }
    }
  }, [updatedTags]);

  const saveDisabled = !(updatedTags.length > 0) || updating;

  return (
    <>
    <Modal {...modalProps}>
      <div>
        <div className='flex items-baseline justify-center pt-4'>
          <p className='font-bold text-lg'>Create & edit tags</p>
          <FontAwesomeIcon
            className='text-[25px] text-neutral-300 cursor-pointer absolute top-6 right-6'
            icon={faTimes}
            onClick={handleCloseModal}
          />
        </div>
        <div className='relative'>
          <div className="flex flex-col m-3.5">
            <TagColorPicker
              selectedTagColor={color}
              onColorSelect={setColor}
            />
            <AddTag
              name={name}
              className="mt-2"
              setName={setName}
              onAddClick={createTag}
              loading={creating}
            />
          </div>
          <hr className="h-[2px] bg-neutral-300 mx-4" />
          <TagList
            tags={tags}
            className="px-3 max-h-[420px]"
            onDeleteClick={setTagToDelete}
            onTagNameChange={onTagNameChangeHandler}
          />
          <div className="flex justify-end mb-2 mx-3">
            <Button
              variant='info'
              className="mr-2"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void updateTags()}
              disabled={saveDisabled}
            >
              {!updating ? 'Save' : <Spinner className="mx-2"/>}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
    <DeleteConfirmationModal
      open={tagToDelete != undefined}
      loading={deleting}
      title="Are you sure you want to delete this tag? All publishables using this tag will be affected."
      onCancel={onDeleteClose}
      onConfirm={onDeleteConfirm}
      confirmCtaText="Delete"
    />
  </>
  );
}
